import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useBrands } from '@/features/brands';
import { ApiError } from '@/shared/api/api-error';
import { SearchableSelect, type SearchableOption } from '@/shared/components/searchable-select';
import { useDebouncedValue } from '@/shared/hooks/use-debounced-value';

import { useCreateLoyaltyBrandRule, useUpdateLoyaltyBrandRule } from '../api/loyalty.queries';
import { loyaltyBrandRuleSchema, type LoyaltyBrandRuleFormValues } from '../schemas/loyalty.schema';
import type { LoyaltyBrandRule } from '../types/loyalty.types';

const FIELDS = ['brandId', 'points'] as const;
const NO_OPTIONS: SearchableOption[] = [];

/**
 * Add a brand rule (points per purchased item for every denomination of the brand, instead of
 * the country default) or change its points. 0 points means the brand explicitly earns nothing.
 */
export function BrandRuleDialog({
  countryId,
  countryName,
  defaultPoints,
  rule,
  onClose,
}: {
  countryId: number;
  countryName: string;
  /** The country's active default, shown so staff can compare; null when disabled. */
  defaultPoints: number | null;
  rule?: LoyaltyBrandRule;
  onClose: () => void;
}) {
  const create = useCreateLoyaltyBrandRule(countryId);
  const update = useUpdateLoyaltyBrandRule();
  const mutation = rule ? update : create;
  const [search, setSearch] = useState('');
  const debounced = useDebouncedValue(search, 300);
  const brands = useBrands({ page: 1, pageSize: 20, search: debounced || undefined, countryId });
  const form = useForm<LoyaltyBrandRuleFormValues>({
    resolver: zodResolver(loyaltyBrandRuleSchema),
    defaultValues: { brandId: rule?.brandId ?? 0, points: rule?.points ?? 0 },
  });

  const { error } = mutation;
  useEffect(() => {
    if (!(error instanceof ApiError) || !error.fieldErrors) return;
    for (const [field, message] of Object.entries(error.fieldErrors)) {
      const key = FIELDS.find((name) => name.toLowerCase() === field.toLowerCase());
      if (key) form.setError(key, { type: 'server', message });
    }
  }, [error, form]);
  const formError = error instanceof ApiError && !error.fieldErrors ? error.message : null;

  const options =
    brands.data?.items.map((brand) => ({
      value: String(brand.id),
      label: brand.name,
      description: brand.category?.nameEn ?? undefined,
    })) ?? NO_OPTIONS;

  const submit = (values: LoyaltyBrandRuleFormValues) => {
    if (rule) {
      update.mutate({ id: rule.id, points: values.points }, { onSuccess: onClose });
      return;
    }
    create.mutate(values, { onSuccess: onClose });
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base">
            {rule ? 'Edit brand rule' : 'New brand rule'}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Every denomination of {rule?.brandName ?? 'the brand'} in {countryName} earns these
            points instead of the default
            {defaultPoints === null ? ' (the default is disabled)' : ` (${defaultPoints} per item)`}
            .
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form noValidate className="space-y-4 text-xs" onSubmit={form.handleSubmit(submit)}>
            {!rule && (
              <FormField
                control={form.control}
                name="brandId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="loyalty-brand-rule-brand">Brand</FormLabel>
                    <SearchableSelect
                      id="loyalty-brand-rule-brand"
                      label="Brand"
                      value={field.value > 0 ? String(field.value) : null}
                      options={options}
                      onChange={(value) => field.onChange(Number(value))}
                      placeholder="Choose a brand"
                      searchPlaceholder="Search brands…"
                      emptyText="No brand matches."
                      search={search}
                      onSearchChange={setSearch}
                      loading={brands.isFetching}
                      triggerClassName="h-9 w-full text-sm"
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            <FormField
              control={form.control}
              name="points"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Points per purchased item</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      inputMode="numeric"
                      min={0}
                      step={1}
                      value={Number.isFinite(field.value) ? field.value : ''}
                      onChange={(event) =>
                        field.onChange(
                          event.target.value === '' ? Number.NaN : Number(event.target.value),
                        )
                      }
                    />
                  </FormControl>
                  <FormDescription>
                    Applies to every denomination of the brand. 0 = this brand earns no points.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            {formError && (
              <p role="alert" className="text-destructive">
                {formError}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={mutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending && <Loader2 className="size-3.5 animate-spin" aria-hidden />}
                {rule ? 'Save' : 'Add rule'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
